/**
 * Settings, shared across the app.
 *
 * Context rather than prop-drilling because the theme, the recipe filters and
 * the expiry band all need the same values and sit in different corners of the
 * tree. It stays a plain object with one updater — no reducer, no actions.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { loadSettings, saveSettings } from "./settingsStorage";
import { DEFAULT_SETTINGS } from "./types";
import type { Settings } from "./types";

interface SettingsValue {
  settings: Settings;
  /** Changes one or more fields and persists the result. */
  update: (patch: Partial<Settings>) => void;
}

const SettingsContext = createContext<SettingsValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  // Starts at the defaults and is replaced after mount, so the first render is
  // identical on the server, in a test, and in a browser with no storage.
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);

  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ settings, update }), [settings, update]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsValue {
  const value = useContext(SettingsContext);
  if (!value) throw new Error("useSettings must be used inside a SettingsProvider");
  return value;
}
